import { Component, OnInit } from '@angular/core';
import { combineLatest, distinctUntilChanged, map, Subscription } from 'rxjs';
import { MediaChange, MediaObserver } from '@angular/flex-layout';
import { Logger, OrganizationService } from 'src/app/@shared';
import { Sort } from '@angular/material/sort';
import { PageEvent } from '@angular/material/paginator';
import { OfferPromo, PromoDomain, OfferPromoService, OfferService, Offer, OfferDomain } from '../..';
import { SelectionModel } from '@angular/cdk/collections';
import { Status } from '../../models/offer-promo-status';
import { AuthorizeControlService } from 'pr1-ui-components';
import { ActivatedRoute, Router } from '@angular/router';
import { FormArray, FormControl, FormGroup } from '@angular/forms';

const log = new Logger('VersionPromoTableComponent');

@Component({
  selector: 'app-version-promo-table',
  templateUrl: './version-promo-table.component.html',
  styleUrls: ['./version-promo-table.component.scss'],
})
export class VersionPromoTableComponent<
  TOfferPromo extends OfferPromo,
  TPromoDomain extends PromoDomain<OfferPromo>,
  T extends Offer,
  TOfferDomain extends OfferDomain<Offer>
> implements OnInit
{
  private assetsUrl: string = '';
  form = new FormGroup({
    versions: new FormArray([])
  });
  viewModel$ = combineLatest([
    this.offerpromoService.getpromoChildVersions$,
    this.offerpromoService.isLoading$,
    this.offerpromoService.totalVersionsRecords$,
    this.offerpromoService.page$,
    this.organizationService.assetsUrl$,
  ]).pipe(
    map(([offers, isLoading, totalRecords, page, assetsUrl]) => {
      if (offers.length == 0 && page.pageIndex > 0) {
        page.previousPageIndex = 0;
        page.pageIndex = 0;
        this.offerService.page(page);
        this.offerService.reload();
      }
      this.form = new FormGroup({
        versions: new FormArray([])
      });
      offers.map((x: any) => this.addVersion(x));
      this.assetsUrl = assetsUrl;
      return { offers, isLoading, totalRecords, page, assetsUrl };
    })
  );
  flexMediaWatcher!: Subscription;
  displayedColumns = [
    'select',
    'Image',
    'Name',
    'Version',
    'Headline',
    'BodyCopy',
    'EventType',
    'VersionCodes',
    'Status',
    'Rank',
    'Actions',
  ];
  offerVersionsList: TOfferDomain[] = [];
  selection = new SelectionModel<TOfferDomain>(true, []);
  constructor(
    private offerpromoService: OfferPromoService<TOfferPromo, TPromoDomain>,
    private organizationService: OrganizationService,
    private mediaObserver: MediaObserver,
    private authorizationService: AuthorizeControlService,
    public offerService: OfferService<T, TOfferDomain>,
    private router: Router,
    private route : ActivatedRoute
  ) {}

  ngOnInit(): void {
    log.debug('init');
    this.offerService.reload();
    this.offerService.getofferChildVersions$.subscribe((data) => {
      this.offerVersionsList = data;
    });

    // detect changes in viewport size to handle show/hide of table columns
    const getAlias = (MediaChange: MediaChange[]) => {
      return MediaChange[0].mqAlias;
    };

    this.flexMediaWatcher = this.mediaObserver
      .asObservable()
      .pipe(
        distinctUntilChanged(
          (x: MediaChange[], y: MediaChange[]) => getAlias(x) === getAlias(y)
        )
      )
      .subscribe((change) => {
        if (change.some((x) => x.mqAlias === 'xs')) {
          this.displayedColumns = ['select', 'Name', 'Actions'];
        } else if (change.some((x) => x.mqAlias === 'sm')) {
          this.displayedColumns = ['select', 'Name', 'Headline', 'Actions'];
        } else {
          this.displayedColumns = [
            'select',
            'Image',
            'Name',
            'Version',
            'Headline',
            'BodyCopy',
            'EventType',
            'VersionCodes',
            'Status',
            'Rank',
            'Actions',
          ];
        }
      });
  }

  onEdit(id: string){
    this.router.navigate([`../../../${id}/promos/promodetails`], { relativeTo: this.route });
  }

  updateStatus(status: Status) {
    let selectedRecordsDetails: any;
    selectedRecordsDetails = JSON.parse(
      JSON.stringify(this.getSelectedSectionRecords())
    );
    this.updateRecordsStatus(selectedRecordsDetails, status);
  }

  updateTableStatus(status: Status, row: any) {
    let selectedRecordsDetails: any;
    selectedRecordsDetails = [JSON.parse(JSON.stringify(row))];
    this.updateRecordsStatus(selectedRecordsDetails, status);
  }

  updateRecordsStatus(selectedRecordsDetails: Array<any>, status: any) {
    if (selectedRecordsDetails && selectedRecordsDetails.length > 0) {
      selectedRecordsDetails = selectedRecordsDetails.map((x: any) => {
        x.Detail.Status = status;
        return x.Detail;
      });
      this.offerpromoService.updateStatus(selectedRecordsDetails).subscribe({
        next: () => {
          this.offerService.reload();
          this.offerpromoService.reload();
          this.clearSelection();
          this.offerpromoService.updateRecordStatus(new Date());
        },
      });
    }
  }

  onSort(sortState: Sort): void {
    this.offerpromoService.childVersionsSort(sortState);
    this.selection.clear();
  }

  onPage(pageEvent: PageEvent): void {
    this.selection.clear();
    this.offerService.page(pageEvent);
  }

  isAllSelected() {
    const numSelected = this.selection.selected.length;
    const numRows = this.offerVersionsList.length;
    return numSelected === numRows;
  }

  /** Selects all rows if they are not all selected; otherwise clear selection. */
  masterToggle() {
    this.isAllSelected()
      ? this.selection.clear()
      : this.offerVersionsList.forEach((row) => this.selection.select(row));
  }

  public getSelectedSectionRecords() {
    return this.selection.selected;
  }

  public clearSelection() {
    this.selection.clear();
  }

  public getImageSrc(assets: any) {
    let imagesrc = '';
    if (assets.Type === 'application/pdf') {
      imagesrc = './assets/images/pdf.png';
    } else if (assets.Type.indexOf('audio') > -1) {
      imagesrc = './assets/images/mp3.jpg';
    } else if (assets.Type.indexOf('video') > -1) {
      imagesrc = './assets/images/mp4.png';
    } else if (assets.Type.indexOf('text') > -1) {
      imagesrc = './assets/images/text.png';
    } else {
      imagesrc = this.assetsUrl + '/' + assets.FileName;
    }
    return imagesrc;
  }

  canEdit(): boolean {
    const hasAccess = this.authorizationService.checkAccess('edit||offermang');
    return hasAccess;
  }

  get versions(): FormArray {
    return this.form.get('versions') as FormArray;
  }

  addVersion(offer: any) {
    if (this.versions) {
      const formcontrolInfo = new FormControl(offer);
      formcontrolInfo.disable();
      this.versions.push(formcontrolInfo);
    }
  }
}
